import { VEHICLES } from '../../data/vehicles';
import { generateId } from '../helpers';
import { MANUFACTURER_FINANCE, computeManufacturerQuote, getManufacturerLoyaltyTier } from '../../data/manufacturerFinance';
import { BUNDLE_DISCOUNT, BUNDLE_SIZE, DEMO_DAYS } from '../../data/tradeShow';
import { defaultCoverageForTier, isCoverageAllowed, minCoverageForTier } from '../../data/insurancePolicy';
import { getVehicleTransferCost } from '../../data/depotGeo';
import { getDepotOccupancy, DEFAULT_DEPOT_CAPACITY } from '../../data/depotEconomics';
import { tryPromotePhase } from '../helpers/phaseProgression';
import { advanceFleetOps, appendFleetOp, createClaimOp, createSaleOp, CLAIM_PAYOUT_DAYS } from '../helpers/fleetOpsLog';

/* ────────────────────────────────────────────────────────────────────
 * 14/06/2026 — Capacité du garage de base.
 * Le garage du QG ne loge que 2 camions au départ : acquérir un 3e
 * véhicule (achat cash, leasing OU financement constructeur) exige
 * l'investissement « Agrandissement dépôt (x2) » (inv-depot-01,
 * 4 places). L'UI (BuyTab/LeaseTab) affiche un bandeau explicite et
 * désactive les boutons ; cette garde slice-level protège tout autre
 * appelant (événements, scripts, tests).
 * ──────────────────────────────────────────────────────────────────── */
export const GARAGE_BASE_CAPACITY = 2;
export const garageExpansionRequired = (state) =>
  (state.vehicles || []).length >= GARAGE_BASE_CAPACITY &&
  !state.activeInvestments?.['inv-depot-01'];

/* ────────────────────────────────────────────────────────────────────
 * 31/05/2026 — 3-day vehicle delivery cycle.
 *
 * Enqueue Nathalie's narrative follow-ups for D+1, D+2, D+3 when a
 * vehicle is purchased / financed / leased AND Nathalie is hired. Silent
 * otherwise. Status flips from 'delivery' → 'idle' on `availableOnDay`
 * inside `coreSlice.nextDay`.
 * ──────────────────────────────────────────────────────────────────── */
function _enqueueDeliveryFollowups(getStore, vehicle, vehicleData) {
  try {
    const state = getStore();
    const hired = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
    if (!hired) return;
    const enqueue = state.enqueueDeferredMessage;
    if (typeof enqueue !== 'function') return;
    const day = state.totalDays || 0;
    const label = `${vehicleData.brand} ${vehicleData.model}`;

    enqueue(day + 1, {
      senderType: 'support',
      senderName: 'Nathalie Robert (Assistante)',
      senderRole: 'role-nathalie-robert',
      tone: 'friendly', kind: 'info',
      subject: `${label} — carte grise demandée`,
      body:
        `Bonjour Patron,\n\n` +
        `J'ai déposé la demande de **carte grise** pour le ${label} ce matin (préfecture en ligne, paiement de la taxe régionale incluse).\n\n` +
        `Délai habituel : 24 à 48 h pour recevoir le titre provisoire. Je vous fais un point demain.\n\n` +
        `— Nathalie`,
      ttlDays: 7, receivedDay: day + 1,
    }, `nathalie-deliv-d1-${vehicle.id}`);

    enqueue(day + 2, {
      senderType: 'support',
      senderName: 'Nathalie Robert (Assistante)',
      senderRole: 'role-nathalie-robert',
      tone: 'friendly', kind: 'info',
      subject: `${label} — carte grise reçue, badges péage en cours`,
      body:
        `Bonjour Patron,\n\n` +
        `**Carte grise reçue** pour le ${label}. Le véhicule est immatriculé.\n\n` +
        `J'ai enchaîné avec :\n` +
        `   • **Badges télépéage** (commandés ce matin, livraison au dépôt sous 24 h)\n` +
        `   • **Carte carburant** (activée sur le réseau partenaire principal)\n` +
        `   • **Assurance flotte** (avenant transmis à votre assureur, prise d'effet immédiate)\n\n` +
        `Le véhicule sera prêt à rouler demain matin.\n\n` +
        `— Nathalie`,
      ttlDays: 7, receivedDay: day + 2,
    }, `nathalie-deliv-d2-${vehicle.id}`);

    enqueue(day + 3, {
      senderType: 'support',
      senderName: 'Nathalie Robert (Assistante)',
      senderRole: 'role-nathalie-robert',
      tone: 'celebratory', kind: 'info',
      subject: `${label} — disponible au dépôt`,
      body:
        `Bonjour Patron,\n\n` +
        `Le **${label}** est livré au dépôt et **opérationnel** dès maintenant. Vous pouvez l'affecter à un chauffeur et un contrat.\n\n` +
        `Bonne route !\n\n` +
        `— Nathalie`,
      ttlDays: 7, receivedDay: day + 3,
    }, `nathalie-deliv-d3-${vehicle.id}`);
  } catch (err) { console.warn('[fleetSlice] delivery follow-up failed:', err); }
}

/* ────────────────────────────────────────────────────────────────────
 * 31/05/2026 — 5-day vehicle resale cycle.
 *
 * Counter-part to the delivery cycle: when the player sells a vehicle,
 * Nathalie orchestrates a 3-step admin saga and the cash lands D+5.
 *   J+1 : expert appraisal sent
 *   J+3 : offer signed
 *   J+5 : virement received + vehicle leaves fleet
 *
 * Silent if Nathalie isn't hired (only the cash timeline still applies).
 * ──────────────────────────────────────────────────────────────────── */
function _enqueueResaleFollowups(getStore, vehicle, finalPrice, completionDay) {
  try {
    const state = getStore();
    const hired = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
    if (!hired) return;
    const enqueue = state.enqueueDeferredMessage;
    if (typeof enqueue !== 'function') return;
    const day = state.totalDays || 0;
    const label = `${vehicle.brand} ${vehicle.model}`;
    // The expert appraisal headlines ~88 % of the final price (volatility
    // illusion before the buyer locks in).
    const apprPrice = Math.round(finalPrice * 0.88);

    enqueue(day + 1, {
      senderType: 'support',
      senderName: 'Nathalie Robert (Assistante)',
      senderRole: 'role-nathalie-robert',
      tone: 'friendly', kind: 'info',
      subject: `${label} — expertise + offre signée`,
      body:
        `Bonjour Patron,\n\n` +
        `**Expertise reçue** pour le ${label} : estimation **${apprPrice.toLocaleString('fr-FR')} €**.\n` +
        `**Offre signée** dans la foulée — prix négocié final **${finalPrice.toLocaleString('fr-FR')} €**.\n\n` +
        `Le compromis a été contresigné par l'acheteur. ` +
        `Virement attendu demain.\n\n` +
        `— Nathalie`,
      ttlDays: 5, receivedDay: day + 1,
    }, `nathalie-sale-d1-${vehicle.id}`);

    // 05/02/2026 — Q1 retour user prod : étape « offre signée » fusionnée avec
    // l'expertise (cycle de revente réduit à 2 jours).

    enqueue(completionDay, {
      senderType: 'support',
      senderName: 'Nathalie Robert (Assistante)',
      senderRole: 'role-nathalie-robert',
      tone: 'celebratory', kind: 'info',
      subject: `${label} — virement reçu`,
      body:
        `Bonjour Patron,\n\n` +
        `**Virement reçu** : ${finalPrice.toLocaleString('fr-FR')} € crédités ce matin. ` +
        `Le ${label} est officiellement sorti de la flotte (radiation immatriculation faite).\n\n` +
        `Bonne continuation !\n\n` +
        `— Nathalie`,
      ttlDays: 7, receivedDay: completionDay,
    }, `nathalie-sale-d5-${vehicle.id}`);

    // 31/05/2026 — Early-resale empathy note. When the player resells a
    // vehicle WITHIN 30 days of buying it (typical bad-decision-corrected
    // pattern), Nathalie lands a short human-tone note the morning AFTER
    // the wire received. Pure narrative — no mechanical effect.
    const daysOwned = day - (vehicle.purchaseDay || 0);
    if (daysOwned >= 0 && daysOwned < 30) {
      enqueue(completionDay + 1, {
        senderType: 'support',
        senderName: 'Nathalie Robert (Assistante)',
        senderRole: 'role-nathalie-robert',
        tone: 'friendly', kind: 'info',
        subject: `Petit mot — sur le ${label}`,
        body:
          `Bonjour Patron,\n\n` +
          `Juste un mot, à titre personnel, sur le dossier ${label}.\n\n` +
          `On a tourné la page proprement — ne soyez pas trop dur avec vous-même. ` +
          `Acheter puis revendre **moins de 30 jours plus tard**, ça arrive à tout le monde — c'est même un signe ` +
          `de **lucidité** d'admettre une mauvaise décision tôt plutôt que de la traîner pendant des années.\n\n` +
          `Le coût administratif a été contenu. On repart de l'avant.\n\n` +
          `— Nathalie`,
        ttlDays: 14, receivedDay: completionDay + 1,
      }, `nathalie-sale-empathy-${vehicle.id}`);
    }
  } catch (err) { console.warn('[fleetSlice] resale follow-up failed:', err); }
}

export const createFleetSlice = (set, get) => ({
  vehicles: [],
  // Manufacturer relations: per-brand state — units financed, loyalty
  // tier, first contact day, and a `defaulted` flag that locks future offers
  // when the player walks away from a loan.
  manufacturerRelations: {},
  // Annual trade show: when present and active, pops a modal at the start
  // of the year-mark tick (totalDays % 360 === 0, after day 0). Stays open
  // until the player closes it; decisions per brand are recorded inside.
  pendingMfgTradeShow: null,
  // 18/09/2026 — Suivi des opérations flotte (ventes) et dossiers sinistres
  // (cf. helpers/fleetOpsLog.js). Alimente le panneau « Suivi des
  // opérations & sinistres » de la page Flotte.
  fleetOpsLog: [],

  buyVehicle: (vehicleDataId) => {
    const state = get();
    const vehicleData = VEHICLES.find(v => v.id === vehicleDataId);
    if (!vehicleData || state.treasury < vehicleData.price) return false;
    // 14/06/2026 — garage de base plein (2 camions) sans Agrandissement dépôt.
    if (garageExpansionRequired(state)) return false;

    // 03/06/2026 — Prime de conversion pour véhicules électriques.
    // Le gouvernement crédite 10 000 € par PL électrique (cf. message
    // presse `news-electric-subsidy-01`). Auto-appliqué à l'achat,
    // notifié via une entrée messagerie info (URSSAF / État) et tracé
    // dans le subsidy ledger pour les rapports comptables.
    const ELECTRIC_SUBSIDY = 10000;
    const isElectric = vehicleData.engineType === 'electric';

    // 31/05/2026 — 3-day delivery cycle. Replaces "magic instant availability".
    //   J+1: carte grise demandée  (Nathalie memo #1 — if hired)
    //   J+2: carte grise reçue + badges péage  (memo #2)
    //   J+3: véhicule livré au dépôt, dispo  (memo #3)
    // The status flips from 'delivery' → 'idle' at availableOnDay in nextDay.
    const newVehicle = {
      id: generateId(),
      dataId: vehicleData.id,
      brand: vehicleData.brand,
      model: vehicleData.model,
      tier: vehicleData.tier,
      image: vehicleData.image || null,
      specialties: vehicleData.specialties,
      price: vehicleData.price,
      maintenancePerMonth: vehicleData.maintenancePerMonth,
      insurancePerMonth: vehicleData.insurancePerMonth,
      dailyFuel: vehicleData.dailyFuel,
      engineType: vehicleData.engineType || 'diesel',
      wear: 0, km: 0,
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      status: 'delivery',
      availableOnDay: state.totalDays + 3,
      assignedDriverId: null,
      assignedContractId: null,
      insuranceCoverage: defaultCoverageForTier(vehicleData.tier),
      depotId: state.activeDepotId || 'depot-hq',
      stateSubsidyReceived: isElectric ? ELECTRIC_SUBSIDY : 0,
    };

    set({
      vehicles: [...state.vehicles, newVehicle],
      treasury: state.treasury - vehicleData.price + (isElectric ? ELECTRIC_SUBSIDY : 0),
    });

    // 18/06/2026 — audit TVA user : l'achat crédite la TVA déductible (20 %,
    // 100 % récupérable sur les PL). Visible dans Fiscalité → TVA.
    let purchaseTva = 0;
    if (typeof get().recordDeductibleTva === 'function') {
      purchaseTva = get().recordDeductibleTva(vehicleData.price, 'vehicle_purchase');
    }

    // 04/06/2026 — User-requested: confirmation email at purchase time
    // with an explicit ETA (J+3 delivery cycle). Mirrors the lease-aid
    // saga but works for cash buys too.
    if (typeof get().pushCustomMessage === 'function') {
      try {
        get().pushCustomMessage({
          senderType: 'supplier',
          senderName: 'Concession partenaire',
          tone: 'positive',
          kind: 'info',
          subject: `Confirmation de commande — ${vehicleData.brand} ${vehicleData.model}`,
          body:
            `Bonjour,\n\nNous accusons réception de votre commande pour le ${vehicleData.brand} ${vehicleData.model} (${(vehicleData.price || 0).toLocaleString('fr-FR')} € HT).\n\n` +
            `📅 **Livraison prévue : J+3** (jour de jeu ${newVehicle.availableOnDay}).\n\n` +
            (purchaseTva > 0
              ? `💶 **TVA déductible enregistrée : ${purchaseTva.toLocaleString('fr-FR')} €** — elle viendra en déduction de votre prochaine déclaration (onglet Fiscalité → TVA).\n\n`
              : '') +
            `Notre équipe logistique se charge de l'immatriculation, des contrôles techniques et des badges péage. Vous recevrez un second email confirmant l'arrivée du véhicule sur votre dépôt.\n\n` +
            `Cordialement,\nVotre concession partenaire`,
          ttlDays: 14,
        });
      } catch (err) { console.warn('[hst-store] order confirmation message failed:', err); }
    }

    // 03/06/2026 — Notify the player of the applied subsidy via messagerie.
    if (isElectric && typeof get().pushCustomMessage === 'function') {
      try {
        get().pushCustomMessage({
          senderType: 'urssaf',
          senderName: 'Administration — Aide à la conversion',
          tone: 'formal',
          kind: 'info',
          subject: `Prime de conversion appliquée — ${ELECTRIC_SUBSIDY.toLocaleString('fr-FR')} €`,
          body:
            `Bonjour,\n\nVotre acquisition d'un poids lourd électrique (${vehicleData.brand} ${vehicleData.model}) ` +
            `vous donne droit à la prime de conversion exceptionnelle de ${ELECTRIC_SUBSIDY.toLocaleString('fr-FR')} €. ` +
            `Le montant a été crédité directement sur votre trésorerie ce jour.\n\n` +
            `Cette aide est cumulable avec les exonérations ZFE en vigueur dans les grandes métropoles.\n\n` +
            `Cordialement,\nMinistère de la Transition Écologique`,
          ttlDays: 30,
        });
      } catch (err) { console.warn('[hst-store] subsidy message push failed:', err); }
    }

    // 31/05/2026 — Enqueue Nathalie's 3-day delivery saga if she's hired.
    _enqueueDeliveryFollowups(get, newVehicle, vehicleData);

    // 08/02/2026 — Refonte phases (Q2). Achat de véhicule :
    //   - Tier 5 → promotion automatique en P4 (international).
    //   - Premier Tier ≥ 2 du jeu → vidéo Sora 2 « vehicle-tier-up »
    //     (montée en gamme).
    if ((vehicleData.tier || 1) >= 5) {
      tryPromotePhase(get, set, 4, `Acquisition d'un véhicule Tier 5 : ${vehicleData.brand} ${vehicleData.model}`);
    }
    if ((vehicleData.tier || 1) >= 2 && typeof get().triggerMilestone === 'function') {
      const seenTierUp = (state.milestonesSeen || []).includes('vehicle_tier_up_first');
      if (!seenTierUp) {
        try {
          get().triggerMilestone('vehicle_tier_up_first', '/flotte');
          if (typeof get().triggerMilestoneVideo === 'function') {
            get().triggerMilestoneVideo({
              kind: 'vehicle-tier-up',
              reason: `${vehicleData.brand} ${vehicleData.model} (Tier ${vehicleData.tier})`,
            });
          }
        } catch (err) { console.warn('[fleetSlice] tier-up video trigger failed:', err); }
      }
    }

    return true;
  },

  /**
   * Get a manufacturer financing quote for a given truck model + duration.
   * Returns null when the brand has no captive arm, or the eligibility object.
   */
  computeManufacturerFinanceQuote: (vehicleDataId, durationMonths) => {
    const state = get();
    const vehicleData = VEHICLES.find(v => v.id === vehicleDataId);
    if (!vehicleData) return null;
    const rel = state.manufacturerRelations?.[vehicleData.brand] || null;
    return computeManufacturerQuote(vehicleData, vehicleData.brand, durationMonths, rel);
  },

  /**
   * Finance a brand-new vehicle through the manufacturer's captive arm.
   * The down-payment is deducted up front from treasury; the remainder
   * becomes a manufacturer-type credit (kind: 'manufacturer') that is
   * processed by the same monthly payment loop as bank credits (a flag
   * differentiates the source for accounting).
   *
   * Relationship tracking:
   *   - units++ in manufacturerRelations[brand]
   *   - firstPurchaseDay set on first deal
   *   - Loyalty tier auto-recalculated when units cross a threshold
   *     (via getManufacturerLoyaltyTier on next read)
   */
  financeVehicleWithManufacturer: (vehicleDataId, durationMonths) => {
    const state = get();
    const vehicleData = VEHICLES.find(v => v.id === vehicleDataId);
    if (!vehicleData) return { ok: false, reason: 'unknown_vehicle' };
    // 14/06/2026 — garage de base plein (2 camions) sans Agrandissement dépôt.
    if (garageExpansionRequired(state)) return { ok: false, reason: 'garage_full' };
    const rel = state.manufacturerRelations?.[vehicleData.brand] || null;
    const quote = computeManufacturerQuote(vehicleData, vehicleData.brand, durationMonths, rel);
    if (!quote) return { ok: false, reason: 'no_captive_finance' };
    if (!quote.eligible) return { ok: false, reason: 'bank_refused', detail: quote.reason };
    if (state.treasury < quote.downPayment) return { ok: false, reason: 'insufficient_down_payment' };

    const credit = {
      id: generateId(),
      kind: 'manufacturer',
      manufacturer: vehicleData.brand,
      bankId: null,
      principal: quote.financed,
      annualRate: quote.rate,
      durationMonths,
      monthlyPayment: quote.monthly,
      remainingMonths: durationMonths,
      remainingPrincipal: quote.financed,
      startDay: state.totalDays,
      // 18/06/2026 — geste commercial « 1ère mensualité offerte » (paliers
      // Client privilégié / Partenaire stratégique) : le constructeur paye
      // la 1ère mensualité (amortissement normal, aucun débit joueur).
      freeMonthsRemaining: quote.firstMonthFree ? 1 : 0,
    };

    const newVehicle = {
      id: generateId(),
      dataId: vehicleData.id,
      brand: vehicleData.brand,
      model: vehicleData.model,
      tier: vehicleData.tier,
      image: vehicleData.image || null,
      specialties: vehicleData.specialties,
      price: vehicleData.price,
      maintenancePerMonth: vehicleData.maintenancePerMonth,
      insurancePerMonth: vehicleData.insurancePerMonth,
      dailyFuel: vehicleData.dailyFuel,
      wear: 0, km: 0,
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      // 31/05/2026 — 3-day delivery cycle (same as cash purchase).
      status: 'delivery',
      availableOnDay: state.totalDays + 3,
      assignedDriverId: null,
      assignedContractId: null,
      insuranceCoverage: defaultCoverageForTier(vehicleData.tier),
      depotId: state.activeDepotId || 'depot-hq',
      financedByManufacturer: vehicleData.brand,
      manufacturerCreditId: credit.id,
    };

    const newRel = { ...(rel || { units: 0, firstPurchaseDay: null, defaulted: false }) };
    newRel.units = (newRel.units || 0) + 1;
    if (!newRel.firstPurchaseDay) newRel.firstPurchaseDay = state.totalDays;

    // Trigger a loyalty-tier upgrade message if we just crossed a threshold.
    const prevTier = getManufacturerLoyaltyTier(vehicleData.brand, newRel.units - 1);
    const newTier  = getManufacturerLoyaltyTier(vehicleData.brand, newRel.units);
    const crossedTier = prevTier && newTier && prevTier.label !== newTier.label;

    set({
      vehicles: [...state.vehicles, newVehicle],
      credits: [...(state.credits || []), credit],
      treasury: state.treasury - quote.downPayment,
      manufacturerRelations: { ...(state.manufacturerRelations || {}), [vehicleData.brand]: newRel },
    });

    // 10/07/2026 (fix user prod) — la promotion Phase 4 sur Tier 5 vaut pour
    // TOUTES les voies d'acquisition, pas seulement l'achat comptant.
    if ((vehicleData.tier || 1) >= 5) {
      tryPromotePhase(get, set, 4, `Acquisition d'un véhicule Tier 5 : ${vehicleData.brand} ${vehicleData.model}`);
    }

    // 31/05/2026 — Nathalie 3-day delivery follow-ups (if hired).
    _enqueueDeliveryFollowups(get, newVehicle, vehicleData);

    // Push messagerie events.
    if (typeof get().pushMessageFromTemplate === 'function') {
      // Always push a confirmation message tagged to the brand.
      get().pushMessageFromTemplate('mfg-finance-confirm-01', {
        brand: vehicleData.brand,
        brandName: MANUFACTURER_FINANCE[vehicleData.brand].name,
        vehicleName: `${vehicleData.brand} ${vehicleData.model}`,
        monthly: quote.monthly,
        durationMonths,
      });
      if (crossedTier) {
        get().pushMessageFromTemplate('mfg-loyalty-upgrade-01', {
          brand: vehicleData.brand,
          brandName: MANUFACTURER_FINANCE[vehicleData.brand].name,
          tierLabel: newTier.label,
          discountPp: ((prevTier.discount - newTier.discount) * 100).toFixed(1),
          rebatePct: Math.round(newTier.residualRebate * 100),
        });
      }
    }

    return { ok: true, quote, credit, vehicle: newVehicle };
  },

  /**
   * 13/06/2026 — Chantier Dépôts Phase 3 : transfert d'un camion vers un
   * autre dépôt du réseau (1 jour de route, 1,2 €/km, min 300 €). Le camion
   * doit être libre (idle, sans contrat). Capacité du dépôt cible vérifiée.
   * Le chauffeur assigné voyage avec son camion (depotId mis à jour).
   */
  transferVehicle: (vehicleId, targetDepotId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return { ok: false, reason: 'not_found' };
    if (vehicle.status !== 'idle' || vehicle.assignedContractId) {
      return { ok: false, reason: 'vehicle_busy' };
    }
    const depots = state.ownedDepots || [];
    const fromDepot = depots.find(d => d.id === (vehicle.depotId || 'depot-hq'));
    const toDepot = depots.find(d => d.id === targetDepotId);
    if (!toDepot) return { ok: false, reason: 'unknown_depot' };
    if ((vehicle.depotId || 'depot-hq') === targetDepotId) return { ok: false, reason: 'same_depot' };
    // 17/06/2026 — Refonte dépôts : cible doit être opérationnelle.
    if (!toDepot.isHeadquarters) {
      const hired = state.hiredDepotManagers || {};
      if (!toDepot.managerId || !hired[toDepot.managerId]) {
        return { ok: false, reason: 'target_depot_not_operational' };
      }
    }
    // 25/07/2026 — plancher défensif : les saves legacy portaient encore
    // capacityVehicles: 5 alors que la base est passée à 10.
    const capacity = Math.max(toDepot.capacityVehicles || 0, DEFAULT_DEPOT_CAPACITY);
    // 23/07/2026 (choix user 2b) — seuls les camions stationnés comptent
    // (cf. getDepotOccupancy) ; l'erreur remonte l'occupation pour l'UI.
    const occupancy = getDepotOccupancy(targetDepotId, state.vehicles, state.activeContracts);
    if (occupancy >= capacity) return { ok: false, reason: 'target_depot_full', capacity, occupancy };
    const cost = getVehicleTransferCost(fromDepot, toDepot, state.company);
    if ((state.treasury || 0) < cost) return { ok: false, reason: 'insufficient_funds', cost };
    set({
      treasury: state.treasury - cost,
      vehicles: state.vehicles.map(v => v.id === vehicleId ? {
        ...v,
        status: 'transfer',
        transferArrivalDay: (state.totalDays || 0) + 1,
        transferTargetDepotId: targetDepotId,
      } : v),
      // Le chauffeur attaché suit le camion vers le nouveau site.
      drivers: vehicle.assignedDriverId
        ? state.drivers.map(d => d.id === vehicle.assignedDriverId ? { ...d, depotId: targetDepotId } : d)
        : state.drivers,
      notifications: [...(state.notifications || []).slice(-30), {
        id: `transfer-${vehicleId}-${state.totalDays}`,
        type: 'vehicle_transfer',
        title: 'Transfert inter-dépôts',
        message: `${vehicle.brand} ${vehicle.model} part pour « ${toDepot.name} » (convoyage −${cost.toLocaleString('fr-FR')} €). Arrivée demain.`,
        day: state.totalDays,
        read: false,
      }],
    });
    return { ok: true, cost };
  },

  /**
   * 17/06/2026 (Lot 4) — Rémi Forni (role-christophe-faure) auto-transfer.
   * Quand le joueur essaye d'accepter un contrat hors zone et qu'aucun de
   * ses camions n'est dans le bon dépôt, Rémi prend la main et transfère
   * automatiquement un camion compatible (idle, sans contrat) depuis un
   * autre dépôt. Pousse un message Messagerie + notification.
   *
   * Stratégie : on cherche d'abord le véhicule initialement choisi par le
   * joueur (vehicleId). S'il est libre, on le transfère. Sinon, on prend
   * n'importe quel camion idle compatible avec le tier requis. Retourne
   * `{ ok: true, vehicleId, arrivalDay }` si l'opération est lancée.
   */
  autoTransferForContract: (preferredVehicleId, targetDepotId, _contract) => {
    const state = get();
    const depots = state.ownedDepots || [];
    const toDepot = depots.find(d => d.id === targetDepotId);
    if (!toDepot) return { ok: false, reason: 'unknown_depot' };
    // 1) Prefer the player's chosen vehicle if it is currently free.
    let candidate = state.vehicles.find(v => v.id === preferredVehicleId
      && v.status === 'idle'
      && !v.assignedContractId);
    // 2) Fallback : any idle vehicle in a non-target depot.
    if (!candidate) {
      candidate = state.vehicles.find(v => v.status === 'idle'
        && !v.assignedContractId
        && (v.depotId || 'depot-hq') !== targetDepotId);
    }
    if (!candidate) return { ok: false, reason: 'no_candidate' };
    // Run the existing transferVehicle action and push a Rémi-flavored msg.
    const transferRes = get().transferVehicle(candidate.id, targetDepotId);
    if (!transferRes || !transferRes.ok) return transferRes || { ok: false, reason: 'transfer_failed' };
    const pushMsg = get().pushCustomMessage;
    if (typeof pushMsg === 'function') {
      try {
        pushMsg({
          senderType: 'support',
          senderName: 'Rémi Forni (Resp. exploitation)',
          senderRole: 'role-christophe-faure',
          tone: 'positive',
          kind: 'info',
          subject: `Transfert automatique vers ${toDepot.name}`,
          body:
            `Patron,\n\n` +
            `J'ai initié le transfert d'un camion (${candidate.brand} ${candidate.model}) vers le dépôt « ${toDepot.name} » pour pouvoir servir ce contrat hors zone. Convoyage : ${transferRes.cost.toLocaleString('fr-FR')} €, arrivée demain.\n\n` +
            `Vous pourrez réaccepter le contrat dès l'arrivée du camion à destination. Je m'occupe de ces logistiques inter-dépôts au fil de l'eau dès qu'un contrat hors zone tombe.\n\n` +
            `— Rémi Forni`,
          ttlDays: 15,
        });
      } catch (err) { console.warn('[remi-auto-transfer] push failed:', err); }
    }
    // 17/06/2026 (Lot 4) — increment annual counter for the support
    // yearly digest (Rémi Forni's KPI).
    set((s) => ({ transfersInitiatedThisYear: (s.transfersInitiatedThisYear || 0) + 1 }));
    return {
      ok: true,
      vehicleId: candidate.id,
      arrivalDay: (state.totalDays || 0) + 1,
      cost: transferRes.cost,
    };
  },

  sellVehicle: (vehicleId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle || vehicle.status === 'active') return false;
    // Mortgaged vehicles serve as collateral for an active loan: blocked
    // until the underlying credit is repaid (early-repay or natural end).
    if (vehicle.mortgaged) return false;
    // 31/05/2026 — Already in a resale cycle? Don't double-list.
    if (vehicle.status === 'selling') return false;
    // 31/05/2026 — Vehicles still in their 3-day delivery cycle cannot be
    // resold (the player just received it — preserves coherence).
    if (vehicle.status === 'delivery') return false;

    const ageDays = state.totalDays - (vehicle.purchaseDay || 0);
    const ageMonths = ageDays / 30;
    const baseSellRate = 0.60;
    const wearPenalty = (vehicle.wear / 100) * 0.15;
    const agePenalty = Math.min(ageMonths * 0.01, 0.20);
    const sellRate = Math.max(0.15, baseSellRate - wearPenalty - agePenalty);
    const sellPrice = Math.round(vehicle.price * sellRate);

    // 05/02/2026 — Q1 retour user prod : cycle de revente raccourci de 5 à
    // 2 jours.
    //   J+1 : expertise + offre signée (Nathalie memo combiné)
    //   J+2 : virement reçu + véhicule sort de la flotte
    const completionDay = state.totalDays + 2;
    const updatedDrivers = state.drivers.map(d =>
      d.assignedVehicleId === vehicleId ? { ...d, assignedVehicleId: null, status: 'idle' } : d
    );
    set({
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId
          ? { ...v, status: 'selling', pendingSalePrice: sellPrice, saleCompletionDay: completionDay }
          : v
      ),
      drivers: updatedDrivers,
      fleetOpsLog: appendFleetOp(state.fleetOpsLog, createSaleOp({
        id: generateId(), vehicle, price: sellPrice, day: state.totalDays, completionDay,
      })),
      notifications: [...(state.notifications || []).slice(-30), {
        id: generateId(),
        type: 'vehicle_sale_started',
        title: 'Mise en vente enregistrée',
        message: `${vehicle.brand} ${vehicle.model} : prix négocié ${sellPrice.toLocaleString('fr-FR')} €. Virement attendu J${completionDay} (suivi dans Flotte → Opérations & sinistres).`,
        day: state.totalDays,
        read: false,
      }],
    });

    // 31/05/2026 — Nathalie's 3-step resale saga (only if hired).
    _enqueueResaleFollowups(get, vehicle, sellPrice, completionDay);

    return { ok: true, pendingSalePrice: sellPrice, completionDay };
  },

  maintainVehicle: (vehicleId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return { ok: false, reason: 'not_found' };
    const cost = vehicle.maintenancePerMonth;
    if (state.treasury < cost) return { ok: false, reason: 'insufficient_funds', cost };

    set({
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId ? { ...v, wear: Math.max(0, v.wear - 40), status: v.status === 'broken' ? 'idle' : v.status, pendingRepair: false } : v
      ),
      treasury: state.treasury - cost,
    });
    return { ok: true, cost };
  },

  /**
   * 17/06/2026 — Bouton d'urgence "Débloquer" pour un véhicule immobilisé.
   * Détache symétriquement le chauffeur et le contrat liés. Idempotent.
   * Cible le bug user "camion + chauffeur restés immobilisés, j'ai dû les
   * vendre/virer pour débloquer".
   */
  forceUnstickVehicle: (vehicleId) => {
    const state = get();
    const v = (state.vehicles || []).find(vv => vv.id === vehicleId);
    if (!v) return { ok: false, reason: 'not_found' };
    const linkedDriverId = v.assignedDriverId;
    const linkedContractId = v.assignedContractId;
    set({
      vehicles: state.vehicles.map(vv => vv.id === vehicleId ? {
        ...vv,
        assignedDriverId: null,
        assignedContractId: null,
        status: vv.status === 'broken' || vv.status === 'delivery' || vv.status === 'selling' || vv.status === 'transfer' ? vv.status : 'idle',
      } : vv),
      drivers: linkedDriverId
        ? state.drivers.map(d => d.id === linkedDriverId ? {
          ...d,
          assignedVehicleId: null,
          status: d.status === 'active' ? 'idle' : d.status,
        } : d)
        : state.drivers,
      // 17/06/2026 — Si un contrat est lié, on le retire de activeContracts
      // (l'utilisateur a déjà décidé de débloquer, on assume une rupture
      // implicite ; pas de pénalité car le bug n'était pas son fait).
      activeContracts: linkedContractId
        ? (state.activeContracts || []).filter(c => c.instanceId !== linkedContractId)
        : state.activeContracts,
    });
    return { ok: true, freedDriverId: linkedDriverId, freedContractId: linkedContractId };
  },

  // Per-phase ceiling on the number of *simultaneously active* leased vehicles.
  // Phase 1 (artisan): keep things manageable. Phase 2 (PME): broader fleet
  // possible but still capped. Phase 3+ (groupe): unlimited.
  // Indexed by `state.phase`, value = max leased vehicles allowed.
  LEASING_CAP_BY_PHASE: { 1: 5, 2: 10, 3: Infinity, 4: Infinity, 5: Infinity },

  leaseVehicle: (vehicleDataId, durationMonths = 36) => {
    const state = get();
    const vehicleData = VEHICLES.find(v => v.id === vehicleDataId);
    if (!vehicleData) return false;
    // 14/06/2026 — garage de base plein (2 camions) sans Agrandissement dépôt.
    if (garageExpansionRequired(state)) return false;
    // Enforce the per-phase cap on leased vehicles.
    const cap = (get().LEASING_CAP_BY_PHASE && get().LEASING_CAP_BY_PHASE[state.phase || 1]) || Infinity;
    const currentLeased = (state.vehicles || []).filter(v => v.isLeased).length;
    if (currentLeased >= cap) {
      // Push a message so the player understands the cap.
      try {
        const pushMsg = get().pushCustomMessage;
        if (pushMsg) {
          pushMsg({
            senderType: 'bank',
            senderName: 'Confrère du leasing',
            tone: 'formal',
            kind: 'warning',
            subject: `Plafond leasing atteint (${cap} véhicules en Phase ${state.phase || 1})`,
            body: `Vous avez atteint le nombre maximum de véhicules en leasing pour votre phase actuelle (${cap}). Pour poursuivre votre développement par leasing, passez en phase supérieure — ou financez le prochain véhicule à l'achat / via crédit bancaire.`,
            ttlDays: 7,
          });
        }
      } catch (err) { console.warn('[hst-fleet] silent failure:', err); }
      return false;
    }
    const deposit = Math.round(vehicleData.price * 0.10);
    if (state.treasury < deposit) return false;
    const monthlyLease = Math.round((vehicleData.price * 0.90) / durationMonths * 1.08);
    const residualValue = Math.round(vehicleData.price * 0.25);

    const leasedVehicle = {
      id: generateId(),
      dataId: vehicleData.id,
      brand: vehicleData.brand,
      model: vehicleData.model,
      tier: vehicleData.tier,
      specialties: vehicleData.specialties,
      price: vehicleData.price,
      maintenancePerMonth: vehicleData.maintenancePerMonth,
      insurancePerMonth: vehicleData.insurancePerMonth,
      dailyFuel: vehicleData.dailyFuel,
      wear: 0, km: 0,
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      // 31/05/2026 — 3-day delivery cycle (lessor handles paperwork too).
      status: 'delivery',
      availableOnDay: state.totalDays + 3,
      assignedDriverId: null,
      assignedContractId: null,
      isLeased: true,
      leaseMonthlyPayment: monthlyLease,
      leaseEndDay: state.totalDays + (durationMonths * 30),
      leaseDuration: durationMonths,
      leaseDeposit: deposit,
      leaseResidualValue: residualValue,
      insuranceCoverage: defaultCoverageForTier(vehicleData.tier),
      depotId: state.activeDepotId || 'depot-hq',
    };

    set({
      vehicles: [...state.vehicles, leasedVehicle],
      treasury: state.treasury - deposit,
    });

    // 10/07/2026 (fix user prod) — un Tier 5 loué débloque aussi la phase 4.
    if ((vehicleData.tier || 1) >= 5) {
      tryPromotePhase(get, set, 4, `Acquisition d'un véhicule Tier 5 : ${vehicleData.brand} ${vehicleData.model}`);
    }

    // 31/05/2026 — Nathalie 3-day delivery follow-ups (if hired).
    _enqueueDeliveryFollowups(get, leasedVehicle, vehicleData);

    return true;
  },

  /**
   * Compute the cost to terminate a leasing contract early.
   * Formula: (remaining monthly payments + residual value) × 1.05 (5% penalty)
   */
  computeLeaseBuyout: (vehicleId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.isLeased) return null;
    const monthsRemaining = Math.max(
      0,
      Math.ceil(((vehicle.leaseEndDay || 0) - state.totalDays) / 30)
    );
    const residual = vehicle.leaseResidualValue || Math.round(vehicle.price * 0.25);
    const monthly = vehicle.leaseMonthlyPayment || 0;
    const remainingMonthlies = monthsRemaining * monthly;
    const subtotal = remainingMonthlies + residual;
    const penalty = Math.round(subtotal * 0.05);
    const total = subtotal + penalty;
    return { monthsRemaining, monthly, remainingMonthlies, residual, penalty, total };
  },

  /**
   * Compute the cost to break a leasing contract early WITHOUT keeping the
   * vehicle (rendition au loueur). Formula: (monthsRemaining × monthly × 0.30)
   * + 1 000 € de frais de remise en état.
   */
  computeLeaseBreak: (vehicleId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.isLeased) return null;
    const monthsRemaining = Math.max(
      0,
      Math.ceil(((vehicle.leaseEndDay || 0) - state.totalDays) / 30)
    );
    const monthly = vehicle.leaseMonthlyPayment || 0;
    const remainingMonthlies = monthsRemaining * monthly;
    const penalty = Math.round(remainingMonthlies * 0.30);
    const adminFee = 1000;
    const total = penalty + adminFee;
    return { monthsRemaining, monthly, remainingMonthlies, penalty, adminFee, total };
  },

  /**
   * Break a lease contract: the vehicle is returned to the lessor (removed
   * from the fleet), the driver is freed, and a break fee is charged.
   */
  breakLease: (vehicleId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.isLeased) return false;
    // Cannot break a lease while the vehicle is on an active contract.
    if (vehicle.status === 'active') return { ok: false, reason: 'on_mission' };
    const quote = get().computeLeaseBreak(vehicleId);
    if (!quote) return false;
    if (state.treasury < quote.total) return { ok: false, reason: 'insufficient' };

    // Free the linked driver (if any).
    const updatedDrivers = state.drivers.map(d =>
      d.assignedVehicleId === vehicleId
        ? { ...d, assignedVehicleId: null, status: 'idle' }
        : d
    );

    set({
      vehicles: state.vehicles.filter(v => v.id !== vehicleId),
      drivers: updatedDrivers,
      treasury: state.treasury - quote.total,
    });
    return { ok: true, ...quote };
  },

  buyoutLease: (vehicleId) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.isLeased) return false;
    const quote = get().computeLeaseBuyout(vehicleId);
    if (!quote) return false;
    if (state.treasury < quote.total) return false;
    set({
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId ? { ...v, isLeased: false, leaseMonthlyPayment: 0, leaseEndDay: null } : v
      ),
      treasury: state.treasury - quote.total,
    });
    return quote;
  },

  /**
   * Buy out a leased vehicle by financing the operation with a bank loan
   * secured by the vehicle itself (mortgage / gage automobile). The loan
   * principal exactly covers the buyout total, so the operation is cash-
   * neutral on the player's treasury today — but adds a new monthly
   * payment until the loan matures.
   *
   * The vehicle is flagged `mortgaged: true` until the loan is repaid:
   *   - cannot be sold while mortgaged
   *   - the mortgage is auto-released when the loan reaches 0 remaining
   *     months (handled in coreSlice.nextDay) or via early repayment
   *     (handled in bankSlice.earlyRepayLoan).
   */
  buyoutLeaseWithLoan: (vehicleId, bankId, durationMonths) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle || !vehicle.isLeased) return { ok: false, reason: 'not_leased' };
    if (vehicle.status === 'active') return { ok: false, reason: 'on_mission' };
    const quote = get().computeLeaseBuyout(vehicleId);
    if (!quote) return { ok: false, reason: 'no_quote' };
    const proposal = typeof get().computeBuyoutLoanQuote === 'function'
      ? get().computeBuyoutLoanQuote(vehicleId, bankId, durationMonths)
      : null;
    if (!proposal) return { ok: false, reason: 'bank_unavailable' };
    if (!proposal.eligible) return { ok: false, reason: 'bank_refused', bankReason: proposal.reason };

    const loan = {
      id: generateId(),
      bankId,
      principal: proposal.amount,
      annualRate: proposal.rate,
      durationMonths,
      monthlyPayment: proposal.monthly,
      remainingMonths: durationMonths,
      remainingPrincipal: proposal.amount,
      startDay: state.totalDays,
      mortgageVehicleId: vehicleId,
    };

    const rel = { ...(state.bankRelations?.[bankId] || { loansRepaid: 0, totalBorrowed: 0, activeLoanCount: 0, firstLoanDay: null }) };
    rel.totalBorrowed = (rel.totalBorrowed || 0) + proposal.amount;
    rel.activeLoanCount = (rel.activeLoanCount || 0) + 1;
    if (!rel.firstLoanDay) rel.firstLoanDay = state.totalDays;

    set({
      credits: [...(state.credits || []), loan],
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId
          ? {
              ...v,
              isLeased: false,
              leaseMonthlyPayment: 0,
              leaseEndDay: null,
              mortgaged: true,
              mortgageLoanId: loan.id,
            }
          : v
      ),
      bankRelations: { ...(state.bankRelations || {}), [bankId]: rel },
      // Treasury net: +loan -buyout = 0 (cash-neutral by design).
    });
    return { ok: true, quote, proposal, loan };
  },

  addUsedVehicle: (vehicleDataId, discountRate, initialWear) => {
    const state = get();
    const vehicleData = VEHICLES.find(v => v.id === vehicleDataId);
    if (!vehicleData) return false;
    const price = Math.round(vehicleData.price * discountRate);
    if (state.treasury < price) return false;

    const usedVehicle = {
      id: generateId(),
      dataId: vehicleData.id,
      brand: vehicleData.brand,
      model: vehicleData.model,
      tier: vehicleData.tier,
      specialties: vehicleData.specialties,
      price: vehicleData.price,
      maintenancePerMonth: vehicleData.maintenancePerMonth,
      insurancePerMonth: vehicleData.insurancePerMonth,
      dailyFuel: vehicleData.dailyFuel,
      wear: initialWear || 25,
      km: Math.round(Math.random() * 80000 + 20000),
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      // 31/05/2026 — 3-day delivery cycle (used vehicles need a carte
      // grise transfer too).
      status: 'delivery',
      availableOnDay: state.totalDays + 3,
      assignedDriverId: null,
      assignedContractId: null,
      isUsed: true,
      purchasePrice: price,
      insuranceCoverage: defaultCoverageForTier(vehicleData.tier),
      depotId: state.activeDepotId || 'depot-hq',
    };

    set({
      vehicles: [...state.vehicles, usedVehicle],
      treasury: state.treasury - price,
    });

    // 10/07/2026 (fix user prod) — un Tier 5 d'occasion débloque aussi la phase 4.
    if ((vehicleData.tier || 1) >= 5) {
      tryPromotePhase(get, set, 4, `Acquisition d'un véhicule Tier 5 : ${vehicleData.brand} ${vehicleData.model}`);
    }

    // 31/05/2026 — Nathalie 3-day delivery follow-ups (if hired).
    _enqueueDeliveryFollowups(get, usedVehicle, vehicleData);

    return true;
  },

  /**
   * File an insurance claim for a vehicle.
   */
  /**
   * Coverage tier metadata.
   *   basic    : -30 % prime, 60 % coverage,  -10 pts trust on first claim
   *   standard: prime nominale, 80 % coverage (default — unchanged behavior)
   *   premium : +35 % prime, 95 % coverage, no penalty on first 2 claims
   */
  setInsuranceCoverage: (vehicleId, tier) => {
    const state = get();
    if (!['basic', 'standard', 'premium'].includes(tier)) return false;
    // 13/06/2026 — Plancher de couverture par tier de véhicule :
    // T3/T4 → standard minimum, T5 → premium obligatoire.
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (vehicle && !isCoverageAllowed(vehicle.tier, tier)) {
      return { ok: false, reason: 'coverage_below_minimum', minimum: minCoverageForTier(vehicle.tier) };
    }
    set({
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId ? { ...v, insuranceCoverage: tier } : v
      ),
    });
    return true;
  },

  /**
   * Effective monthly insurance premium for a vehicle, taking coverage tier
   * into account. Default = standard (base premium).
   */
  getVehicleInsurancePremium: (vehicleId) => {
    const v = get().vehicles.find(vv => vv.id === vehicleId);
    if (!v) return 0;
    const tier = v.insuranceCoverage || 'standard';
    const multiplier = tier === 'basic' ? 0.70 : tier === 'premium' ? 1.35 : 1;
    return Math.round((v.insurancePerMonth || 0) * multiplier);
  },

  /**
   * File an insurance claim for a vehicle. Coverage rate now depends on tier.
   */
  fileInsuranceClaim: (vehicleId, repairCost) => {
    const state = get();
    const vehicle = state.vehicles.find(v => v.id === vehicleId);
    if (!vehicle) return null;

    const claims = vehicle.insuranceClaims || [];
    if (claims.length >= 3) return { error: 'max_claims', message: 'Maximum de 3 sinistres atteint. Assurance refusée.' };

    // Base coverage by tier, then -20 pts per prior penalised claim.
    // "Free claims" = how many claims the tier absorbs at full coverage.
    //   basic    : 0 free (every claim past the first is penalised)
    //   standard: 1 free (claim 1 at 80%, claim 2 at 60%, …) — matches original behaviour
    //   premium : 2 free (claims 1 & 2 at 95%, claim 3 starts at 0.20 penalty)
    const tier = vehicle.insuranceCoverage || 'standard';
    const baseByTier = tier === 'basic' ? 0.60 : tier === 'premium' ? 0.95 : 0.80;
    const freeClaims = tier === 'premium' ? 2 : tier === 'basic' ? 0 : 1;
    const currentClaimNumber = claims.length + 1;
    const penalty = Math.max(0, currentClaimNumber - freeClaims) * 0.20;
    const coverageRate = Math.max(0.20, baseByTier - penalty);
    const franchise = Math.round(repairCost * (1 - coverageRate));
    const reimbursement = Math.round(repairCost * coverageRate);

    const claim = {
      id: generateId(),
      day: state.totalDays,
      repairCost,
      reimbursement,
      franchise,
      coverageRate,
      tier,
      claimNumber: claims.length + 1,
    };

    // 18/09/2026 — Retour apprenant : « aucun retour visible sur la prise en
    // charge ». Le sinistre devient un DOSSIER suivi : déclaration reçue →
    // dossier en cours (J+1) → indemnisation versée (J+3). La réparation est
    // payée immédiatement, l'indemnité est créditée par tickFleetOps.
    const payoutDay = state.totalDays + CLAIM_PAYOUT_DAYS;
    const op = createClaimOp({
      id: generateId(), vehicle, claimType: 'repair', day: state.totalDays,
      repairCost, reimbursement, franchise, coverageRate, tier,
    });
    set({
      vehicles: state.vehicles.map(v =>
        v.id === vehicleId
          ? { ...v, insuranceClaims: [...claims, claim], wear: Math.max(0, (v.wear || 0) - 30), status: v.status === 'broken' ? 'idle' : v.status }
          : v
      ),
      treasury: state.treasury - repairCost,
      fleetOpsLog: appendFleetOp(state.fleetOpsLog, op),
      notifications: [...(state.notifications || []).slice(-30), {
        id: generateId(),
        type: 'insurance_claim',
        title: 'Sinistre déclaré — dossier ouvert',
        message: `${vehicle.brand} ${vehicle.model} : réparation ${repairCost.toLocaleString('fr-FR')} € réglée. Indemnisation attendue ${reimbursement.toLocaleString('fr-FR')} € (couverture ${Math.round(coverageRate * 100)} %, franchise ${franchise.toLocaleString('fr-FR')} €) — versement prévu J${payoutDay}.`,
        day: state.totalDays,
        read: false,
      }],
    });

    if (typeof get().pushCustomMessage === 'function') {
      try {
        get().pushCustomMessage({
          senderType: 'supplier',
          senderName: 'AssurFlotte — Service sinistres',
          tone: 'formal',
          kind: 'info',
          subject: `Dossier sinistre n°${op.id.slice(-6).toUpperCase()} — ${vehicle.brand} ${vehicle.model} : déclaration reçue`,
          body:
            `Bonjour,\n\nNous accusons réception de votre déclaration de sinistre pour le **${vehicle.brand} ${vehicle.model}** (dossier n°${op.id.slice(-6).toUpperCase()}).\n\n` +
            `• Montant des réparations : **${repairCost.toLocaleString('fr-FR')} €**\n` +
            `• Couverture ${tier} : ${Math.round(coverageRate * 100)} % → indemnisation prévue **${reimbursement.toLocaleString('fr-FR')} €**\n` +
            `• Franchise à votre charge : ${franchise.toLocaleString('fr-FR')} €\n\n` +
            `Le dossier passe en instruction dès demain ; le virement interviendra le **jour ${payoutDay}**. Vous pouvez suivre son statut dans Flotte → « Opérations & sinistres ».\n\n` +
            `— AssurFlotte`,
          ttlDays: 21,
        });
      } catch (err) { console.warn('[fleetSlice] claim message failed:', err); }
    }

    return { ...claim, vehicleName: `${vehicle.brand} ${vehicle.model}`, opId: op.id, payoutDay };
  },

  /**
   * 18/09/2026 — Avance les dossiers (ventes + sinistres) au jour courant.
   * Appelé après le set() final de nextDay. Crédite les indemnités échues,
   * pousse notifications + emails de confirmation.
   */
  tickFleetOps: () => {
    const state = get();
    const log = state.fleetOpsLog || [];
    if (log.length === 0) return null;
    const day = state.totalDays || 0;
    const { log: nextLog, payouts, saleCredited } = advanceFleetOps(log, day);
    const changed = nextLog.some((op, i) => op !== log[i]);
    if (!changed) return null;
    const credited = payouts.reduce((s, op) => s + (op.amount || 0), 0);
    const notifications = [...(state.notifications || [])];
    payouts.forEach(op => {
      notifications.push({
        id: generateId(),
        type: 'insurance_paid',
        title: 'Indemnisation versée',
        message: `${op.vehicleLabel} : ${op.amount.toLocaleString('fr-FR')} € crédités par l'assureur (dossier n°${op.id.slice(-6).toUpperCase()}).`,
        day,
        read: false,
      });
    });
    set({ fleetOpsLog: nextLog, treasury: state.treasury + credited, notifications: notifications.slice(-40) });

    const pushMsg = get().pushCustomMessage;
    if (typeof pushMsg !== 'function') return { payouts, saleCredited };
    payouts.forEach(op => {
      try {
        pushMsg({
          senderType: 'supplier',
          senderName: 'AssurFlotte — Service sinistres',
          tone: 'positive',
          kind: 'info',
          subject: `Dossier n°${op.id.slice(-6).toUpperCase()} — indemnisation acceptée : ${op.amount.toLocaleString('fr-FR')} € versés`,
          body:
            `Bonjour,\n\nVotre dossier sinistre **${op.vehicleLabel}** est clos : **indemnisation acceptée**.\n\n` +
            `• Montant versé : **${op.amount.toLocaleString('fr-FR')} €** (crédité ce jour J${day})\n` +
            (op.franchise ? `• Franchise retenue : ${op.franchise.toLocaleString('fr-FR')} €\n` : '') +
            `• Couverture appliquée : ${Math.round((op.coverageRate || 0) * 100)} %\n\n— AssurFlotte`,
          ttlDays: 21,
        });
      } catch (err) { console.warn('[fleetSlice] payout message failed:', err); }
    });
    // Nathalie (si recrutée) envoie déjà son mémo « virement reçu ».
    const nathalie = !!(state.hiredSupport && state.hiredSupport['role-nathalie-robert']);
    if (!nathalie) {
      saleCredited.forEach(op => {
        try {
          pushMsg({
            senderType: 'supplier',
            senderName: 'Concession partenaire — Service reprise',
            tone: 'positive',
            kind: 'info',
            subject: `Vente ${op.vehicleLabel} — virement de ${op.amount.toLocaleString('fr-FR')} € reçu`,
            body:
              `Bonjour,\n\nLa vente de votre **${op.vehicleLabel}** est finalisée.\n\n` +
              `• Montant crédité : **${op.amount.toLocaleString('fr-FR')} €**\n` +
              `• Date de crédit : jour ${day} (mise en vente J${op.createdDay})\n\n` +
              `Le véhicule est sorti de votre flotte ; le montant apparaît dans votre trésorerie du jour.\n\nCordialement,\nVotre concession partenaire`,
            ttlDays: 14,
          });
        } catch (err) { console.warn('[fleetSlice] sale message failed:', err); }
      });
    }
    return { payouts, saleCredited };
  },

  maintainAllVehicles: (opts = {}) => {
    const state = get();
    // 09/02/2026 — Q2 Lot I user prod : ne pas réparer les véhicules en
    // mission par défaut. On les marque `pendingRepair: true` ; tickVehicles
    // pushera une notification quand ils repassent en 'idle'.
    // 18/09/2026 — Retour apprenant « Tout réparer n'a jamais fonctionné » :
    // option `includeOnMission` (entretien pendant la mission, comme le
    // bouton Réparer individuel) + retour d'erreur explicite au lieu de null.
    const includeOnMission = !!opts.includeOnMission;
    const locked = (v) => v.status === 'delivery' || v.status === 'transfer' || v.status === 'selling';
    const isOnMission = (v) => v.status === 'active' || !!v.assignedContractId;
    const needs = (v) => (v.wear >= 15 || v.status === 'broken') && !locked(v);
    const eligible = state.vehicles.filter(v => needs(v) && (includeOnMission || !isOnMission(v)));
    const skipped = state.vehicles.filter(v => needs(v) && !includeOnMission && isOnMission(v));
    if (eligible.length === 0 && skipped.length === 0) return { ok: false, reason: 'nothing_to_repair' };
    const totalCost = eligible.reduce((sum, v) => sum + v.maintenancePerMonth, 0);
    if (state.treasury < totalCost) return { ok: false, reason: 'insufficient_funds', cost: totalCost, count: eligible.length };

    const skippedIds = new Set(skipped.map(v => v.id));
    const eligibleIds = new Set(eligible.map(v => v.id));
    set({
      vehicles: state.vehicles.map(v => {
        if (skippedIds.has(v.id)) return { ...v, pendingRepair: true };
        return eligibleIds.has(v.id)
          ? { ...v, wear: Math.max(0, (v.wear || 0) - 40), status: v.status === 'broken' ? 'idle' : v.status, pendingRepair: false }
          : v;
      }),
      treasury: state.treasury - totalCost,
    });
    return { ok: true, count: eligible.length, cost: totalCost, skippedCount: skipped.length, skippedNames: skipped.map(v => `${v.brand} ${v.model}`) };
  },

  // ════════════════════════════════════════════════════════════════════════
  // ANNUAL MANUFACTURER TRADE SHOW
  // ════════════════════════════════════════════════════════════════════════

  /**
   * Accept the 30-day demo offer for one brand. Adds a `isDemo` vehicle to
   * the fleet that auto-deletes itself on day demoExpiresOn. No money is
   * exchanged. The player can assign drivers + contracts to it like any
   * other truck (revenues/expenses count normally during the demo).
   */
  acceptTradeShowDemo: (brandId) => {
    const state = get();
    const show = state.pendingMfgTradeShow;
    if (!show || !show.active) return { ok: false, reason: 'no_show' };
    const offer = (show.brandOffers || []).find(o => o.brandId === brandId);
    if (!offer) return { ok: false, reason: 'no_offer' };
    if (show.decisions?.[brandId]) return { ok: false, reason: 'already_decided' };
    const vd = VEHICLES.find(v => v.id === offer.demo.vehicleDataId);
    if (!vd) return { ok: false, reason: 'unknown_model' };

    const demoVehicle = {
      id: generateId(),
      dataId: vd.id,
      brand: vd.brand,
      model: vd.model,
      tier: vd.tier,
      image: vd.image || null,
      specialties: vd.specialties,
      price: vd.price,
      maintenancePerMonth: vd.maintenancePerMonth,
      insurancePerMonth: vd.insurancePerMonth,
      dailyFuel: vd.dailyFuel,
      wear: 0, km: 0,
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      status: 'idle',
      assignedDriverId: null,
      assignedContractId: null,
      isDemo: true,
      demoExpiresOn: state.totalDays + DEMO_DAYS,
      demoBrand: brandId,
      insuranceCoverage: defaultCoverageForTier(vd.tier),
      depotId: state.activeDepotId || 'depot-hq',
    };

    set({
      vehicles: [...state.vehicles, demoVehicle],
      pendingMfgTradeShow: {
        ...show,
        decisions: { ...(show.decisions || {}), [brandId]: 'demo' },
      },
    });

    if (typeof get().pushMessageFromTemplate === 'function') {
      get().pushMessageFromTemplate('mfg-finance-confirm-01', {
        brand: brandId,
        brandName: MANUFACTURER_FINANCE[brandId].name,
        vehicleName: `${vd.brand} ${vd.model} (DÉMO ${DEMO_DAYS}j)`,
        monthly: 0,
        durationMonths: DEMO_DAYS,
      });
    }
    return { ok: true, vehicle: demoVehicle };
  },

  /**
   * Accept the bulk-order bundle (3 trucks at -8 %). Charges treasury and
   * creates 3 fleet vehicles immediately. The discount is applied only on
   * the catalogue price; insurance/maintenance/fuel keep their nominal
   * values to avoid breaking the running monthly P&L calculations.
   */
  acceptTradeShowBundle: (brandId) => {
    const state = get();
    const show = state.pendingMfgTradeShow;
    if (!show || !show.active) return { ok: false, reason: 'no_show' };
    const offer = (show.brandOffers || []).find(o => o.brandId === brandId);
    if (!offer) return { ok: false, reason: 'no_offer' };
    if (show.decisions?.[brandId]) return { ok: false, reason: 'already_decided' };
    const cost = offer.bundle.totalDiscountedPrice;
    if (state.treasury < cost) return { ok: false, reason: 'insufficient_funds', need: cost };
    const vd = VEHICLES.find(v => v.id === offer.bundle.vehicleDataId);
    if (!vd) return { ok: false, reason: 'unknown_model' };

    const newVehicles = Array.from({ length: BUNDLE_SIZE }, () => ({
      id: generateId(),
      dataId: vd.id,
      brand: vd.brand,
      model: vd.model,
      tier: vd.tier,
      image: vd.image || null,
      specialties: vd.specialties,
      price: vd.price,
      maintenancePerMonth: vd.maintenancePerMonth,
      insurancePerMonth: vd.insurancePerMonth,
      dailyFuel: vd.dailyFuel,
      wear: 0, km: 0,
      daysActive: 0,
      totalRevenue: 0,
      purchaseDay: state.totalDays,
      // 31/05/2026 — 3-day delivery cycle (bundle deliveries are
      // staggered like the rest — fleet logic is consistent).
      status: 'delivery',
      availableOnDay: state.totalDays + 3,
      assignedDriverId: null,
      assignedContractId: null,
      insuranceCoverage: defaultCoverageForTier(vd.tier),
      depotId: state.activeDepotId || 'depot-hq',
      tradeShowBundle: { brand: brandId, year: show.year, discount: BUNDLE_DISCOUNT },
    }));

    // Loyalty units bump (3 trucks = 3 units towards next tier).
    const rel = { ...(state.manufacturerRelations?.[brandId] || { units: 0, firstPurchaseDay: state.totalDays, defaulted: false }) };
    rel.units = (rel.units || 0) + BUNDLE_SIZE;
    if (!rel.firstPurchaseDay) rel.firstPurchaseDay = state.totalDays;

    set({
      vehicles: [...state.vehicles, ...newVehicles],
      treasury: state.treasury - cost,
      manufacturerRelations: { ...(state.manufacturerRelations || {}), [brandId]: rel },
      pendingMfgTradeShow: {
        ...show,
        decisions: { ...(show.decisions || {}), [brandId]: 'bundle' },
      },
    });

    // 10/07/2026 (fix user prod) — un bundle contenant un Tier 5 débloque la phase 4.
    if (newVehicles.some(v => (v.tier || 1) >= 5)) {
      tryPromotePhase(get, set, 4, 'Acquisition de véhicules Tier 5 (bundle salon)');
    }

    if (typeof get().pushMessageFromTemplate === 'function') {
      get().pushMessageFromTemplate('mfg-finance-confirm-01', {
        brand: brandId,
        brandName: MANUFACTURER_FINANCE[brandId].name,
        vehicleName: `Commande groupée — 3 × ${vd.brand} ${vd.model}`,
        monthly: 0,
        durationMonths: 0,
      });
    }

    // 31/05/2026 — Nathalie 3-day delivery follow-ups for the bundle
    // (single saga for the 3 trucks to avoid 9 messages).
    _enqueueDeliveryFollowups(get, newVehicles[0], { ...vd, brand: vd.brand, model: `${vd.model} (×3)` });

    return { ok: true, vehicles: newVehicles, cost, savings: offer.bundle.totalListPrice - cost };
  },

  /**
   * Skip a brand's booth at the trade show without taking the offers.
   */
  skipTradeShowBooth: (brandId) => {
    const state = get();
    const show = state.pendingMfgTradeShow;
    if (!show || !show.active) return false;
    set({
      pendingMfgTradeShow: {
        ...show,
        decisions: { ...(show.decisions || {}), [brandId]: 'skip' },
      },
    });
    return true;
  },

  /**
   * Close the trade show modal — any brand not explicitly decided is
   * considered "skip". Once closed, no more decisions until next year.
   */
  closeTradeShow: () => {
    set({ pendingMfgTradeShow: null });
  },
});
